import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plane, Cog, Boxes, X, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAircraftDropdown, getEnginesDropdown, getComponentsDropdown } from '@/services/api';

/**
 * AssetSelector - Reusable component for selecting aircraft, engines, and components
 * Used in Incidents, Risks, Audits, and CAPAs for linking safety events to assets
 */
export default function AssetSelector({
  value = {}, // { aircraft_id, aircraft_registration, engine_id, engine_serial, component_id, component_part_number }
  onChange,
  showEngines = true,
  showComponents = true,
  showLabels = true,
  compact = false,
  className = '',
}) {
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [aircraft, setAircraft] = useState([]);
  const [engines, setEngines] = useState([]);
  const [components, setComponents] = useState([]);
  
  const [selectedAircraftId, setSelectedAircraftId] = useState(value.aircraft_id || '');
  const [selectedEngineId, setSelectedEngineId] = useState(value.engine_id || '');
  const [selectedComponentId, setSelectedComponentId] = useState(value.component_id || '');

  // Fetch asset lists
  const fetchAssets = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [acRes, engRes, compRes] = await Promise.all([
        getAircraftDropdown(api),
        showEngines ? getEnginesDropdown(api) : Promise.resolve({ data: [] }),
        showComponents ? getComponentsDropdown(api) : Promise.resolve({ data: [] }),
      ]);
      setAircraft(acRes.data || []);
      setEngines(engRes.data || []);
      setComponents(compRes.data || []);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  }, [api, showEngines, showComponents]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Update parent when selection changes
  const handleAircraftChange = (aircraftId) => {
    const ac = aircraft.find(a => a.id === aircraftId);
    setSelectedAircraftId(aircraftId);
    // Clear engine and component if aircraft changes
    setSelectedEngineId('');
    setSelectedComponentId('');
    onChange?.({
      aircraft_id: aircraftId || null,
      aircraft_registration: ac?.registration || null,
      aircraft_type: ac?.aircraft_type || null,
      engine_id: null,
      engine_serial: null,
      component_id: null,
      component_part_number: null,
    });
  };

  const handleEngineChange = (engineId) => {
    const eng = engines.find(e => e.id === engineId);
    setSelectedEngineId(engineId);
    onChange?.({
      ...value,
      aircraft_id: selectedAircraftId || value.aircraft_id,
      engine_id: engineId || null,
      engine_serial: eng?.serial_number || null,
    });
  };

  const handleComponentChange = (componentId) => {
    const comp = components.find(c => c.id === componentId);
    setSelectedComponentId(componentId);
    onChange?.({
      ...value,
      aircraft_id: selectedAircraftId || value.aircraft_id,
      component_id: componentId || null,
      component_part_number: comp?.part_number || null,
      component_serial_number: comp?.serial_number || null,
    });
  };

  const clearSelection = () => {
    setSelectedAircraftId('');
    setSelectedEngineId('');
    setSelectedComponentId('');
    onChange?.({
      aircraft_id: null,
      aircraft_registration: null,
      aircraft_type: null,
      engine_id: null,
      engine_serial: null,
      component_id: null,
      component_part_number: null,
    });
  };

  // Filter engines and components by selected aircraft
  const filteredEngines = selectedAircraftId
    ? engines.filter(e => e.installed_aircraft_id === selectedAircraftId || !e.installed_aircraft_id)
    : engines;

  const filteredComponents = selectedAircraftId
    ? components.filter(c => c.installed_aircraft_id === selectedAircraftId || !c.installed_aircraft_id)
    : components;

  const hasSelection = selectedAircraftId || selectedEngineId || selectedComponentId;

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 py-2", className)}>
        <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
        <span className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
          Loading assets...
        </span>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header with clear button */}
      {showLabels && (
        <div className="flex items-center justify-between">
          <Label className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-gray-700")}>
            <Plane className="w-4 h-4 inline-block mr-2" />
            Link to Asset
          </Label>
          {hasSelection && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="h-6 text-xs text-slate-400 hover:text-white"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      )}

      <div className={cn(
        "grid gap-3",
        compact ? "grid-cols-3" : "grid-cols-1 md:grid-cols-3"
      )}>
        {/* Aircraft Selector */}
        <div>
          {!compact && showLabels && (
            <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
              Aircraft
            </Label>
          )}
          <Select value={selectedAircraftId || 'none'} onValueChange={(v) => handleAircraftChange(v === 'none' ? '' : v)}>
            <SelectTrigger 
              className={cn(
                "w-full",
                isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-300"
              )}
              data-testid="asset-aircraft-select"
            >
              <div className="flex items-center gap-2">
                <Plane className="w-4 h-4 text-sky-500 flex-shrink-0" />
                <SelectValue placeholder="Select aircraft..." />
              </div>
            </SelectTrigger>
            <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "bg-white")}>
              <SelectItem value="none">No aircraft</SelectItem>
              {aircraft.map(ac => (
                <SelectItem key={ac.id} value={ac.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">{ac.registration}</span>
                    <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                      {ac.aircraft_type}
                    </span>
                    {ac.status === 'aog' && (
                      <Badge className="bg-red-500/20 text-red-400 text-xs">AOG</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Engine Selector */}
        {showEngines && (
          <div>
            {!compact && showLabels && (
              <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
                Engine
              </Label>
            )}
            <Select 
              value={selectedEngineId || 'none'} 
              onValueChange={(v) => handleEngineChange(v === 'none' ? '' : v)}
            >
              <SelectTrigger 
                className={cn(
                  "w-full",
                  isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-300"
                )}
                data-testid="asset-engine-select"
              >
                <div className="flex items-center gap-2">
                  <Cog className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <SelectValue placeholder="Select engine..." />
                </div>
              </SelectTrigger>
              <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "bg-white")}>
                <SelectItem value="none">No engine</SelectItem>
                {filteredEngines.map(eng => (
                  <SelectItem key={eng.id} value={eng.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{eng.serial_number}</span>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        {eng.engine_type}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Component Selector */}
        {showComponents && (
          <div>
            {!compact && showLabels && (
              <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
                Component
              </Label>
            )}
            <Select 
              value={selectedComponentId || 'none'} 
              onValueChange={(v) => handleComponentChange(v === 'none' ? '' : v)}
            >
              <SelectTrigger 
                className={cn(
                  "w-full",
                  isDark ? "bg-slate-950 border-slate-800" : "bg-white border-gray-300"
                )}
                data-testid="asset-component-select"
              >
                <div className="flex items-center gap-2">
                  <Boxes className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <SelectValue placeholder="Select component..." />
                </div>
              </SelectTrigger>
              <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "bg-white")}>
                <SelectItem value="none">No component</SelectItem>
                {filteredComponents.map(comp => (
                  <SelectItem key={comp.id} value={comp.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{comp.part_number}</span>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        {comp.category}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {hasSelection && !compact && (
        <div className={cn(
          "flex flex-wrap gap-2 pt-2 border-t",
          isDark ? "border-slate-800" : "border-gray-200"
        )}>
          {selectedAircraftId && (
            <Badge className="bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <Plane className="w-3 h-3 mr-1" />
              {aircraft.find(a => a.id === selectedAircraftId)?.registration}
            </Badge>
          )}
          {selectedEngineId && (
            <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
              <Cog className="w-3 h-3 mr-1" />
              {engines.find(e => e.id === selectedEngineId)?.serial_number}
            </Badge>
          )}
          {selectedComponentId && (
            <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30">
              <Boxes className="w-3 h-3 mr-1" />
              {components.find(c => c.id === selectedComponentId)?.part_number}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AssetDisplay - Read-only display of linked assets
 */
export function AssetDisplay({ 
  aircraftId, 
  aircraftRegistration,
  engineId, 
  engineSerial,
  componentId, 
  componentPartNumber,
  className = '' 
}) {
  const { isDark } = useTheme();
  
  const hasAsset = aircraftId || engineId || componentId;
  
  if (!hasAsset) {
    return (
      <span className={cn("text-sm", isDark ? "text-slate-500" : "text-gray-400")}>
        No linked assets
      </span>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {(aircraftId || aircraftRegistration) && (
        <Badge className="bg-sky-500/20 text-sky-400 border border-sky-500/30">
          <Plane className="w-3 h-3 mr-1" />
          {aircraftRegistration || aircraftId}
        </Badge>
      )}
      {(engineId || engineSerial) && (
        <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
          <Cog className="w-3 h-3 mr-1" />
          {engineSerial || engineId}
        </Badge>
      )}
      {(componentId || componentPartNumber) && (
        <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30">
          <Boxes className="w-3 h-3 mr-1" />
          {componentPartNumber || componentId}
        </Badge>
      )}
    </div>
  );
}
